/**
 * What every voice call in the window does, whoever carries its audio
 * (RFC 0307 3a).
 *
 * An adapter knows how to reach its provider and read its events. Everything
 * around that is the same for any provider: one call per window, a hang-up
 * while connecting that lets go at once, a single teardown that runs exactly
 * once, `onEnd` only for a call that went live, and the person's level for the
 * bars beside the mic. That lives here, so a new provider is its connection
 * and nothing else.
 */

import { claimTheLine, hangUpAllCalls } from './liveCalls'
import type { VoiceCall, VoiceConnectOptions } from './types'

/** The error a call hung up while connecting rejects with. */
export function hungUp(): Error {
  return new DOMException('the call was hung up', 'AbortError')
}

export interface CallLine {
  /** Throw if the person hung up, or the call already ended. */
  throwIfHungUp(): void
  /**
   * Something to release when the call ends: a mic, a peer, a session. Runs at
   * once if the call has already ended, so a resource acquired after a
   * hang-up is still let go.
   */
  onTeardown(release: () => void): void
  /** Take the line, ending every other call in the window. */
  claim(): void
  /** The call is up: from here an end is reported through `onEnd`. */
  live(): void
  /** End the call, once. Reports through `onEnd` only if it went live. */
  end(reason?: { error?: string }): void
  readonly ended: boolean
  /** The person's input level, 0..1, for the bars beside the mic. */
  readonly level: LevelMeter
}

/**
 * Open a line for a call about to connect.
 *
 * Hangs up any other call first (a hot reload's orphan, a second owner), and
 * throws if the person already hung up. Until `live()`, a failure is the
 * adapter's to throw: the caller is still awaiting `connect` and has nothing
 * to end yet.
 */
export function openLine(
  opts: Pick<VoiceConnectOptions, 'signal' | 'onEnd'>,
): CallLine {
  hangUpAllCalls()
  const { signal } = opts
  if (signal?.aborted) throw hungUp()
  const teardown: Array<() => void> = []
  const level = levelMeter()
  let ended = false
  let connected = false
  let release = () => {}

  const end = (reason: { error?: string } = {}) => {
    if (ended) return
    ended = true
    signal?.removeEventListener('abort', onAbort)
    release()
    level.stop()
    // Last acquired, first released: the session before the mic under it.
    for (const fn of teardown.reverse()) {
      try {
        fn()
      } catch {
        // Already closed has nothing left to release.
      }
    }
    if (connected) opts.onEnd?.(reason)
  }
  // A hang-up while connecting lets go of everything now, not when the
  // provider happens to answer.
  const onAbort = () => end()
  signal?.addEventListener('abort', onAbort, { once: true })

  return {
    throwIfHungUp() {
      if (ended || signal?.aborted) throw hungUp()
    },
    onTeardown(fn) {
      if (ended) fn()
      else teardown.push(fn)
    },
    claim() {
      release = claimTheLine(() => end())
    },
    live() {
      connected = true
      signal?.removeEventListener('abort', onAbort)
    },
    end,
    get ended() {
      return ended
    },
    level,
  }
}

export interface LevelMeter {
  /** Start reporting, reading the raw level (0..1 or more) each frame. */
  start(read: () => number): void
  stop(): void
  setMuted(muted: boolean): void
  onLevel: VoiceCall['onLevel']
}

function levelMeter(): LevelMeter {
  const listeners = new Set<(level: number) => void>()
  let muted = false
  let frame = 0
  return {
    start(read) {
      const tick = () => {
        const level = muted ? 0 : Math.min(1, read())
        for (const l of listeners) l(level)
        frame = requestAnimationFrame(tick)
      }
      frame = requestAnimationFrame(tick)
    },
    stop() {
      cancelAnimationFrame(frame)
    },
    setMuted(m) {
      muted = m
    },
    onLevel(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

/**
 * A level read off a mic stream, for a provider that hands us the stream.
 * The audio context is released with the call.
 */
export function micLevel(line: CallLine, mic: MediaStream): () => number {
  const ctx = new AudioContext()
  line.onTeardown(() => void ctx.close().catch(() => {}))
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  ctx.createMediaStreamSource(mic).connect(analyser)
  const samples = new Uint8Array(analyser.fftSize)
  return () => {
    analyser.getByteTimeDomainData(samples)
    let sum = 0
    for (const v of samples) sum += ((v - 128) / 128) ** 2
    return Math.sqrt(sum / samples.length) * 4
  }
}
